import { Box } from 'braid-design-system';
import isNaN from 'lodash/isNaN';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Sticky from 'react-sticky-el';
import * as styles from './StickySerpElement.css';

const transitionDuration = 600; // See ContentSpacer.less component

const getPosition = (selector: string, bottom = false): number | null => {
  const htmlElement = document.querySelector(selector);
  const coordinates = htmlElement && htmlElement.getBoundingClientRect();
  if (!coordinates) {
    return null;
  }
  const positionAttribute = bottom ? 'bottom' : 'top';
  return coordinates?.[positionAttribute];
};

interface StickySerpElementProps {
  children: ReactNode;
  target: string;
  isBelowAnchor?: boolean;
  topOffset?: number;
}

interface setStickyPositionProps
  extends Pick<StickySerpElementProps, 'target' | 'isBelowAnchor'> {
  setAnchorOffset: (anchorOffset: number) => void;
}

const setStickyPosition = ({
  target,
  isBelowAnchor,
  setAnchorOffset,
}: setStickyPositionProps) => {
  setTimeout(() => {
    const stickyPosition = getPosition(`#${target}`);
    const anchorPosition = getPosition(`[name=${target}]`, isBelowAnchor);
    if (anchorPosition && stickyPosition) {
      const anchorOffset = anchorPosition - stickyPosition;
      setAnchorOffset(anchorOffset);
    }
  }, transitionDuration + 1);
};

const StickySerpElement = ({
  children,
  target,
  isBelowAnchor,
  topOffset = 0,
}: StickySerpElementProps) => {
  const [anchorOffset, setAnchorOffset] = useState(0);

  useEffect(() => {
    setStickyPosition({ target, isBelowAnchor, setAnchorOffset });
  }, [target, isBelowAnchor]);

  if (isNaN(anchorOffset)) {
    return null;
  }

  return (
    <Box id={target} className={styles.root}>
      <Sticky
        stickyClassName={styles.whenSticky}
        topOffset={anchorOffset + topOffset}
      >
        <Box width="full" className={styles.stickyElementWrapper}>
          {children}
        </Box>
      </Sticky>
    </Box>
  );
};

export default StickySerpElement;
